import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Drawer, toast } from "@outpave/ds";
import { Button, FormControlLabel, Typography } from "@mui/material";
import { Stack } from "components/layout/reusableLayouts";
import { useFormik, setNestedObjectValues } from "formik";
import { useMutation, useQuery } from "@apollo/client";
import { useAuth } from "@frontegg/react";
import CardFormDrawer from "components/common/cards/CardFormDrawer";
import type { Employee } from "../cards/EmployeeSearch";
import { GET_USER, ADD_USER } from "../../../graphql/user";
import DisplayUserInformation from "components/common/users/DisplayUserInformation";
import UserInformation from "components/common/users/UserInformation";
import StatusType from "../../../enums/UserStatus";
import { FormikAddUser } from "hooks/useUserProfileData";
import { generateUserValidationSchema } from "../../../validations/users";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import CustomFieldParentType from "enums/CustomFieldParentType";
import dayjs from "utils/dayjs";
import { useLogoForScheme } from "utils/themeContextHelper";
import { useUserMentions } from "context/UserMentionProvider";

interface AddNewUserDrawerProps {
  open: boolean;
  handleCloseDrawer: () => void;
  onAfterClose?: () => void;
  handleCreatedUser: (error?: string | null, user?: Employee) => void;
}

/**
 * The created user, in the shape the one consumer that reads it wants. Card
 * issuance needs `phone` and `birthdate`, so dropping either here makes
 * EmployeeSearch's "missing information" notice permanent.
 */
const employeeFromForm = (values: FormikAddUser, id: string): Employee => ({
  id,
  firstName: values.firstName,
  lastName: values.lastName,
  email: values.email,
  phone: values.phone,
  birthdate: values.birthdate,
  address: values.address,
  hasPhysicalCard: values.hasPhysicalCard,
});

const AddNewUserDrawer: React.FC<AddNewUserDrawerProps> = (props) => {
  const { handleCreatedUser, open, handleCloseDrawer, onAfterClose } = props;
  const { refetch: refetchGlobalUsers } = useUserMentions();

  const logo = useLogoForScheme();
  const { user } = useAuth();

  const [step, setStep] = useState<number>(0);
  // Holds the new user as the cardholder, since closing resets the form while the card drawer slides out.
  const cardDrawer = useDrawerRecord<Employee>();

  const {
    customFieldSettings,
    customFieldsInitialValues,
    transformCustomFieldsToArray,
  } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.USER,
  });

  const validationSchema = generateUserValidationSchema(customFieldSettings);

  // The company default the location picker fills in, held as a starting
  // value so an untouched form stays clean.
  const [startLocation, setStartLocation] = useState<FormikAddUser["location"]>(
    { id: "", addressName: "" },
  );

  const formikAddUser = useFormik<FormikAddUser>({
    initialValues: {
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      employeeGroup: "",
      phone: "",
      status: StatusType.PENDING,
      address: {
        addressName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipcode: "",
        country: "US",
      },
      location: startLocation,
      assignedManager: {
        id: "",
        firstName: "",
        lastName: "",
        email: "",
      },
      userPassword: "",
      role: "",
      sendOneTimePassword: true,
      createAndAssignNewCard: false,
      birthdate: null,
      customFields: customFieldsInitialValues,
      company: {
        id: "",
        businessLegalName: "",
      },
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const {
        firstName,
        lastName,
        role,
        email,
        phone,
        status,
        address,
        assignedManager,
        sendOneTimePassword,
        birthdate,
        customFields,
        location,
        createAndAssignNewCard,
        companyId,
      } = values;

      addUser({
        variables: {
          input: {
            companyId: companyId || userData?.user?.company?.id,
            departmentId: userData?.user?.department?.id,
            firstName: firstName,
            middleName: "",
            lastName: lastName,
            title: "Other",
            role: role,
            isOwner: false,
            email: email,
            phone: phone,
            status: status,
            address: {
              addressName: address.addressName,
              address1: address.address1,
              address2: address.address2,
              city: address.city,
              state: address.state,
              zipcode: address.zipcode.toString(),
              country: "US",
            },
            location: {
              id: location.id,
            },
            managerId: assignedManager.id,
            sendOneTimePassword: sendOneTimePassword,
            birthdate: dayjs(birthdate).valueOf(),
            customFields: transformCustomFieldsToArray(customFields),
          },
        },
      });
    },
  });

  const { data: userData } = useQuery(GET_USER, {
    variables: { userId: user?.id },
    skip: Boolean(!user?.id),
  });

  const [addUser, { loading }] = useMutation(ADD_USER, {
    onCompleted: ({ addUser }) => {
      setStep(0);

      const updatedUser = {
        ...formikAddUser.values,
        id: addUser.id,
      };

      if (formikAddUser.values.createAndAssignNewCard) {
        formikAddUser.setValues(updatedUser);
        cardDrawer.show({
          ...employeeFromForm(updatedUser, addUser.id),
          hasPhysicalCard: false,
        });
      } else {
        formikAddUser.resetForm();
        handleCreatedUser(null, employeeFromForm(updatedUser, addUser.id));
        handleCloseDrawer();
      }
      refetchGlobalUsers();
      toast.success(t("User created successfully!"));
    },
    onError: (e) => {
      handleCreatedUser(e.message);
      toast.error(t("Failed to create user") + ": " + e.message);
    },
  });

  const { t } = useTranslation();

  const handleReset = () => {
    setStep(0);

    formikAddUser.resetForm();
    handleCloseDrawer();
  };

  const handleCloseCreateDrawer = () => {
    // Read the form before resetting it: this path ran after the card drawer
    // closed, and it was handing back `addUser`, the mutation trigger.
    const created = employeeFromForm(
      formikAddUser.values,
      formikAddUser.values.id ?? "",
    );
    formikAddUser.resetForm();
    handleCreatedUser(null, created);
    handleCloseDrawer();
    cardDrawer.close();
  };

  // The X, backdrop, Escape and Cancel all leave here, and leaving resets the
  // form because the drawer stays mounted.
  const { requestLeave, dialog } = useUnsavedChangesGuard(
    formikAddUser.dirty,
    handleReset,
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={requestLeave}
        onAfterClose={() => {
          if (!cardDrawer.record) onAfterClose?.();
        }}
        title={t("Add User")}
        loading={loading}
        footer={
          <>
            <Button variant="primary-outlined" onClick={requestLeave}>
              {t("Cancel")}
            </Button>

            {step === 1 && (
              <Button
                variant="primary-soft"
                onClick={async () => {
                  setStep(step - 1);
                }}
              >
                {t("Back")}
              </Button>
            )}

            {step === 0 ? (
              <Button
                variant="primary-filled"
                onClick={async () => {
                  const errors = await formikAddUser.validateForm();
                  if (Object.keys(errors).length !== 0) {
                    formikAddUser.setTouched(
                      setNestedObjectValues(errors, true),
                    );
                    return;
                  }
                  setStep(step + 1);
                }}
              >
                {t("Next")}
              </Button>
            ) : (
              <Button
                variant="primary-filled"
                onClick={() => formikAddUser.handleSubmit()}
              >
                {t("Create")}
              </Button>
            )}
          </>
        }
      >
        {step === 0 && (
          <UserInformation
            formik={formikAddUser}
            customFieldSettings={customFieldSettings}
            onDefaultLocation={setStartLocation}
          />
        )}
        {step === 1 && (
          <form onSubmit={formikAddUser.handleSubmit}>
            <DisplayUserInformation
              handleClickEdit={() => {
                setStep(0);
              }}
              formik={formikAddUser}
              includeIdentity
            />
            <Stack gap="var(--space-4)" sx={{ marginTop: "var(--space-9)" }}>
              <FormControlLabel
                checked={formikAddUser.values.sendOneTimePassword}
                control={
                  <Checkbox
                    value={formikAddUser.values.sendOneTimePassword}
                    name="sendOneTimePassword"
                    onChange={formikAddUser.handleChange}
                  />
                }
                label={
                  <Typography variant="label" component="span">
                    {t(
                      "Send one-time password and notify new employee of their invitation to",
                    )}
                    <img
                      src={logo}
                      alt="Company Logo"
                      style={{
                        height: 16,
                        width: "auto",
                        marginLeft: 8,
                        marginBottom: 2,
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                      draggable={false}
                    />
                  </Typography>
                }
              />
              <FormControlLabel
                checked={formikAddUser.values.createAndAssignNewCard}
                control={
                  <Checkbox
                    value={formikAddUser.values.createAndAssignNewCard}
                    name="createAndAssignNewCard"
                    onChange={(value) => {
                      formikAddUser.handleChange(value);
                    }}
                  />
                }
                label={
                  <Typography variant="label" component="span">
                    {t("Create and Assign New Card")}
                  </Typography>
                }
              />
            </Stack>
          </form>
        )}
      </Drawer>
      {dialog}

      {cardDrawer.record && (
        <CardFormDrawer
          open={cardDrawer.open}
          cardholder={cardDrawer.record}
          onClose={handleCloseCreateDrawer}
          onAfterClose={() => {
            cardDrawer.afterClose();
            onAfterClose?.();
          }}
        />
      )}
    </>
  );
};

export default AddNewUserDrawer;
